import { useState, useEffect, useCallback } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import { format } from 'date-fns'
import SortingIndicator from '@/components/SortingIndicator'
import TableListNoDataRow from '@/components/TableListNoDataRow'
import EditInput from '@/components/trade/EditInput'
import { marketMapState } from '@/recoil/Market'
import { showCancelAllConfirmationPopupState } from '@/recoil/OrderForm'
import fetcher from '@/api/fetcher'
import numberFormat from '@/utils/numberFormat'
import { alphabeticalSort, numericSort } from '@/utils/sorting'
import {
  ORDER_SIDE,
  ORDER_TYPE_MAP,
  MARKET_ORDER_TYPE_LIST,
  ORDER_TYPE_TEXT_MAP,
  ORDER_LIST_HEADER_COLUMNS
} from '@/constants/order'

import './OrderList.css'

function toggleSortingOrder(sorting) {
  return sorting === 'ascending' ? 'descending' : 'ascending'
}

function dataConvert(key, a) {
  if (key === 'type') {
    return a === ORDER_TYPE_MAP.postOnly ? 'Yes' : '-'
  }
  if (['created_time', 'updated_time'].includes(key)) {
    return new Date(a)
  }
}

export default function OrderList({
  isLoading,
  setIsLoading,
  orderType,
  orderList
}) {
  const [marketMap] = useRecoilState(marketMapState)
  const setShowCancelAllConfirmationPopup = useSetRecoilState(
    showCancelAllConfirmationPopupState
  )
  const [brokerMap, setBrokerMap] = useState({})
  const [sorting, setSorting] = useState({})
  const [editOrderKey, setEditOrderKey] = useState('')
  const isOpenOrder = orderType === 'OPEN_ORDERS'
  const isCompletedOrder = orderType === 'FILL_ORDERS'
  const updateSorting = useCallback(
    ({ key, defaultSortingOrder }) =>
      setSorting(prevSorting => {
        const sorting = { key }
        if (prevSorting.key === key) {
          if (prevSorting.order === defaultSortingOrder) {
            sorting.order = toggleSortingOrder(prevSorting.order)
          } else {
            return {}
          }
        } else {
          sorting.order = defaultSortingOrder
        }
        return sorting
      }),
    [setSorting]
  )
  const cancelOrder = useCallback(
    (symbol, orderId) => {
      setIsLoading(true)
      fetcher.cancelOrder(symbol, orderId).finally(() => setIsLoading(false))
    },
    [setIsLoading]
  )

  const onCancelAll = () => setShowCancelAllConfirmationPopup(true)

  useEffect(() => {
    fetcher.getBrokerList().then(brokerList => {
      const map = {}
      brokerList.forEach(({ broker_id, broker_name }) => {
        map[broker_id] = broker_name
      })
      setBrokerMap(map)
    })
  }, [])

  useEffect(() => {
    if (!sorting.key) {
      return
    }

    const hasDataColumn = ORDER_LIST_HEADER_COLUMNS.some(
      ({ key, isFilledOrderData }) =>
        key === sorting.key &&
        (isFilledOrderData === undefined ||
          (!isCompletedOrder && isFilledOrderData === false) ||
          (isCompletedOrder && isFilledOrderData))
    )

    !hasDataColumn && setSorting({})
  }, [isCompletedOrder, sorting])

  const filteredOrderList = sorting.key
    ? orderList.concat([]).sort((a, b) => {
        const { key, order } = sorting
        const isNumericSort = [
          'average_executed_price',
          'price',
          'executed',
          'quantity',
          'order_value',
          'created_time',
          'updated_time'
        ].includes(key)
        const sortFunction = isNumericSort ? numericSort : alphabeticalSort
        const isAsc = order === 'ascending'

        if (key === 'visible') {
          a = a.quantity === a.visible ? '-' : 'Yes'
          b = b.quantity === b.visible ? '-' : 'Yes'
        } else if (key === 'order_value') {
          a = BigNumber(a.price).times(a.quantity).toNumber()
          b = BigNumber(b.price).times(b.quantity).toNumber()
        } else if (key === 'reduce_only') {
          a = a.reduce_only ? 'Yes' : '-'
          b = b.reduce_only ? 'Yes' : '-'
        } else {
          const needDataConvert = [
            'type',
            'created_time',
            'updated_time'
          ].includes(key)
          a = needDataConvert ? dataConvert(key, a[key]) : a[key]
          b = needDataConvert ? dataConvert(key, b[key]) : b[key]
        }
        return sortFunction(a, b, isAsc)
      })
    : orderList

  const resetEditOrderKey = (isLoading = null) => {
    isLoading !== null && setIsLoading(isLoading)
    setEditOrderKey('')
  }
  const OrderList = filteredOrderList.map(
    ({
      order_id,
      client_order_id,
      broker_id,
      symbol,
      type,
      reduce_only,
      side,
      price,
      average_executed_price,
      quantity,
      executed,
      created_time,
      updated_time, // For filled order
      visible
    }) => {
      const [marketType, baseCurrency, quoteCurrency] = symbol.split('_')
      const { priceDecimal = 0 } = marketMap[symbol] || {}
      const marketSymbol =
        marketType === 'PERP'
          ? `${baseCurrency}-PERP`
          : `${baseCurrency}/${quoteCurrency}`
      const isBuyOrder = side === ORDER_SIDE.buy
      const isHiddenOrder =
        visible !== quantity && !MARKET_ORDER_TYPE_LIST.includes(type)
      const orderPrice = isCompletedOrder ? average_executed_price : price
      const orderQuantity = isCompletedOrder ? executed : quantity
      const orderValue = BigNumber(orderPrice).times(orderQuantity).toFixed()
      average_executed_price =
        average_executed_price === null
          ? '-'
          : numberFormat(average_executed_price, priceDecimal)
      const orderTime = isCompletedOrder ? updated_time : created_time

      const onEditOrder = (key, value) => {
        const data = {
          order_id,
          symbol,
          side,
          order_type: type,
          order_price: price,
          order_quantity: quantity,
          [key]: value
        }
        if (client_order_id) {
          data.client_order_id = client_order_id
        }
        if (broker_id) {
          data.broker_id = broker_id
        }
        if (isHiddenOrder) {
          data.visible_quantity = visible
        }
        if (reduce_only) {
          data.reduce_only = reduce_only
        }
        return fetcher.editOrder(data)
      }
      return (
        <tr
          key={order_id}
          className="table-list-row"
          title={brokerMap[broker_id]}
        >
          <td className="table-list-padding-cell" />
          <td className="table-list-cell align-left">{marketSymbol}</td>
          <td className="table-list-cell align-left">
            {ORDER_TYPE_TEXT_MAP[type]}
          </td>
          <td
            className={clsx('table-list-cell align-left', {
              'color-positive': isBuyOrder,
              'color-negative': !isBuyOrder
            })}
          >
            {side}
          </td>
          {isCompletedOrder && (
            <td className="table-list-cell align-right">
              {average_executed_price}
            </td>
          )}
          <td className="table-list-cell align-right">
            {isCompletedOrder ? (
              price !== null ? (
                numberFormat(price, priceDecimal)
              ) : (
                '-'
              )
            ) : (
              <EditInput
                type="order_price"
                symbol={symbol}
                value={price}
                isFocus={editOrderKey === `${order_id}_order_price`}
                onFocus={() => setEditOrderKey(`${order_id}_order_price`)}
                onBlur={resetEditOrderKey}
                onEditOrder={onEditOrder}
                calculateOrderValue={v =>
                  BigNumber(v).times(quantity).toNumber()
                }
              />
            )}
          </td>
          <td className="table-list-cell align-right">{executed}</td>
          <td className="table-list-cell align-right">
            {isCompletedOrder ? (
              quantity !== null ? (
                quantity
              ) : (
                '-'
              )
            ) : (
              <EditInput
                type="order_quantity"
                symbol={symbol}
                value={BigNumber(quantity).minus(executed).toNumber()}
                isFocus={editOrderKey === `${order_id}_order_quantity`}
                onFocus={() => setEditOrderKey(`${order_id}_order_quantity`)}
                onBlur={resetEditOrderKey}
                onEditOrder={onEditOrder}
                calculateOrderValue={v => BigNumber(price).times(v).toNumber()}
              />
            )}
          </td>
          <td className="table-list-cell align-right">
            {numberFormat(orderValue, 0)}
          </td>
          <td className="table-list-cell align-left">
            {type === ORDER_TYPE_MAP.postOnly ? 'Yes' : '-'}
          </td>
          <td className="table-list-cell align-left">
            {reduce_only ? 'Yes' : '-'}
          </td>
          <td className="table-list-cell align-left">
            {isHiddenOrder ? 'Yes' : '-'}
          </td>
          <td className="table-list-cell align-right">
            {format(new Date(orderTime), 'yyyy-MM-dd HH:mm:ss')}
          </td>
          {!isCompletedOrder && (
            <td className="table-list-cell align-right">
              <button
                className="cancel-order-button"
                onClick={() => cancelOrder(symbol, order_id)}
              >
                Cancel
              </button>
            </td>
          )}
          <td className="table-list-padding-cell" />
        </tr>
      )
    }
  )

  const HeaderColumnList = []
  for (let i = 0; i < ORDER_LIST_HEADER_COLUMNS.length; i++) {
    const {
      key,
      title,
      align,
      customClass = [],
      isSortable = true,
      isFilledOrderData,
      defaultSortingOrder = 'descending'
    } = ORDER_LIST_HEADER_COLUMNS[i]

    const show =
      isFilledOrderData === undefined ||
      (!isCompletedOrder && isFilledOrderData === false) ||
      (isCompletedOrder && isFilledOrderData)

    show &&
      HeaderColumnList.push(
        <th
          key={key}
          className={clsx('table-list-cell', `align-${align}`, customClass)}
        >
          {isSortable ? (
            <div
              className="sorting-label noselect"
              onClick={() => updateSorting({ key, defaultSortingOrder })}
            >
              {title}
              <SortingIndicator
                isActive={sorting.key === key}
                order={sorting.order}
              />
            </div>
          ) : (
            <>{title}</>
          )}
        </th>
      )
  }

  const orderListNotEmpty = orderList.length > 0

  return (
    <div
      className={clsx('order-list-container', {
        loading: isLoading
      })}
    >
      <table className="table-list">
        <thead>
          <tr className="table-list-header">
            <th className="table-list-padding-cell" />
            {HeaderColumnList}
            {!isCompletedOrder && (
              <td className="table-list-cell align-right action-column">
                {isOpenOrder && orderListNotEmpty && (
                  <button className="cancel-order-button" onClick={onCancelAll}>
                    Cancel All
                  </button>
                )}
              </td>
            )}
            <th className="table-list-padding-cell" />
          </tr>
        </thead>
        <tbody>
          {orderListNotEmpty ? (
            OrderList
          ) : (
            <TableListNoDataRow addPaddingCell={true} />
          )}
        </tbody>
      </table>
    </div>
  )
}
